import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, TableCell, TableRow, Typography, Stack, alpha } from "@mui/material";
import { getSecurityAsync, getSecurityKey } from "../../util/security";
import { useSelector } from "react-redux";
import { OptionType, SecurityCategory_CategoryCode } from "../../proto/generated/security";
import { useStyles } from "./styles";
import { ExtraSmallButton, NonInteractiveTooltip } from "../custom";
import ExitIcon from "../../assets/icons/ExitIcon";
import { selectOrderType, selectTradeOrders, selectTradeProduct } from "../../selectors/strategyBuilder";
import { selectOrderBook } from "../../selectors/security";
import { getSquareOffOrder } from "../../util/positions";
import { useDispatch } from "react-redux";
import { updateTradeOrder, updateTradeOrders } from "../../reducer/strategyBuilder.reducer";
import LtpCell from "./ltpCell";
import { toLocaleStringIn } from "../../util/general";
import ProfitCell from "./profitCell";
import { AnalyzeGroupedPosition } from "../../reducer/positions.reducer";
import { selectGroupBy } from "../../selectors/positions";
import { getPositionProduct } from "../../util/positions";
import { exchangeToJSON } from '../../proto/generated/common.ts'
import { Broker_OrderType, OrderStatus, OrderType, productToJSON, TransactionType } from '../../proto/generated/order.ts'
import { selectOrdersHistory } from "../../selectors/orders";

export type AnalyzePositionsTableRowProps = {
    position: AnalyzeGroupedPosition,
    expandEnabled?: boolean,
    child?: boolean
}


const AnalyzePositionsTableRow: React.FC<AnalyzePositionsTableRowProps> = ({position, expandEnabled, child}) => {
    const dispatch = useDispatch();
    const { classes, cx } = useStyles();
    const {security: sec, quantity, avgPrice, groupId, children, squaredOffQuantity, addedQuantity, newQuantity} = position;
    const [security, setSecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const securityKey = useMemo(() => getSecurityKey(sec.exchange, sec.code), [sec])
    useEffect(() => { if (securityKey) getSecurityAsync(securityKey).then(setSecurity); else setSecurity(undefined) }, [securityKey])
    const groupBy = useSelector(selectGroupBy);
    const tradeOrders = useSelector(selectTradeOrders);
    const orderBook = useSelector(selectOrderBook(securityKey));
    const orderBookRef = useRef(orderBook);
    const strategyProductType = useSelector(selectTradeProduct)
    const orderType = useSelector(selectOrderType)
    const ordersHistory = useSelector(selectOrdersHistory)

    useEffect(() => {
        orderBookRef.current = orderBook;
    }, [orderBook])
    
    const executedSquareOffQuantity = useMemo(() => {
        if (child || !position.originalPosition || position.quantity === 0) return 0;
        
        const executedSquareOffOrders = (ordersHistory || []).filter(o => {
            const isSameBasket = o.basketId === position.groupId;
            const isSameSecurity = o.security.code === position.security.code;
            const isExecuted = o.status === OrderStatus.EXECUTED;
            const isOppositeDirection = position.quantity > 0 ? o.transactionType === TransactionType.SELL : o.transactionType === TransactionType.BUY;
            return isSameBasket && isSameSecurity && isExecuted && isOppositeDirection && ![OrderType.STOP_LOSS, OrderType.TARGET].includes(o.orderType);
        });
        
        if (executedSquareOffOrders.length > 0) {
            return executedSquareOffOrders.reduce((sum, o) => sum + o.quantity, 0);
        }
        
        return 0;
    }, [child, position, ordersHistory])

    const displayName = useMemo(() => {
        if (groupBy === "basket" && children?.length > 1) {
            return position.groupName;
        }

        const sec = security
        let result = sec?.symbol
        if (sec?.categoryCode === SecurityCategory_CategoryCode.OPTIONS) {
            const expiry = new Date(sec?.expiry?.expiry ?? 0)
            const day = expiry.getDate()
            const month = expiry.toLocaleString('en', { month: 'short' }).toUpperCase()
            const year = expiry.getFullYear()
            const currentYear = new Date().getFullYear()
            if (year === currentYear) {
                result = `${day} ${month}`
            } else {
                result = `${day} ${month} ${year.toString().slice(-2)}`
            }
            result += ` ${(sec.strikePrice ?? 0) / (sec.priceMultiplier ?? 100)} ${sec.optionType === OptionType.CE ? 'CE' : 'PE'}`
        }
        return result
    }, [security, groupId, groupBy, position, children])

    const onSquareOffClick = useCallback(() => {
        const securityKey = getSecurityKey(position.security.exchange, position.security.code);
        const existingOrder = tradeOrders.find(order => getSecurityKey(order.security.exchange, order.security.code) === securityKey);

        let currentOrderBook = orderBookRef.current;
        const price = position.quantity > 0 ? currentOrderBook?.bids[0]?.price ?? 0 : currentOrderBook?.asks[0]?.price ?? 0;
        const squareOffOrder = getSquareOffOrder(position, price)

        if (existingOrder) {
            dispatch(updateTradeOrder({
                clientOrderId: existingOrder.clientOrderId,
                update: {
                    ...squareOffOrder,
                    security
                }
            }))
        } else {
            dispatch(updateTradeOrders([...tradeOrders, {...squareOffOrder, security}]))
        }
    }, [position, security, tradeOrders])

    const quantityDisplay = useMemo(() => {
        let nodes: any[] = [];

        if (newQuantity !== quantity || squaredOffQuantity || addedQuantity) {
            nodes.push(<span key="old" className={cx(
                classes.oldQtyDisplay,
                quantity > 0 ? classes.buyQuantity: undefined,
                quantity < 0 ? classes.sellQuantity: undefined
            )}>{quantity}</span>)
        }

        nodes.push(<span key="new" className={cx(newQuantity > 0 ? classes.buyQuantity: undefined ,
            newQuantity < 0 ? classes.sellQuantity : undefined
        )}>{newQuantity}</span>)

        return nodes;

    }, [quantity, squaredOffQuantity, addedQuantity, newQuantity])

    // For incoming positions, display "MARKET" or "LIMIT" text instead of price
    const displayAvgPrice = useMemo(() => {
        // Check if this is an incoming position (has addedQuantity)
        const isIncoming = (position.addedQuantity ?? 0) !== 0;

        if (isIncoming) {
            // For incoming positions, show "MARKET" or "LIMIT" text
            // Check if the order price is 0 (MARKET) or has a price (LIMIT)
            if (orderType == Broker_OrderType.MARKET_ORDER) {
                return 'Market';
            } else {
                return 'Limit';
            }
        }

        // For existing positions, show the actual avgPrice
        if (avgPrice && avgPrice !== 0) {
            return toLocaleStringIn(avgPrice / (security?.priceMultiplier ?? 100));
        }

        return '-';
    }, [avgPrice, position.addedQuantity, security?.priceMultiplier, orderType])

    const isIncomingOrder = useMemo(() => quantity === 0 && newQuantity !== 0, [quantity, newQuantity])

    const isPartialSquareOff = useMemo(() => {
        const hasPendingPartialSquareOff = (squaredOffQuantity ?? 0) !== 0 && (newQuantity ?? 0) !== 0 && (addedQuantity ?? 0) === 0;
        if (hasPendingPartialSquareOff) return true;
        return executedSquareOffQuantity > 0;
    }, [squaredOffQuantity, newQuantity, addedQuantity, executedSquareOffQuantity])

    return  <>
                {!isPartialSquareOff && <TableRow className={ cx(classes.tableRow, newQuantity === 0 ? classes.closedPositionRow : undefined)}>
                    <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: '12px',
                                    lineHeight: 1,
                                    overflow: 'hidden',
                                    whiteSpace: 'nowrap',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                    flexShrink: 1,
                                    minWidth: 0
                                }}
                            >
                                {displayName}
                            </Typography>
                            {!(groupBy === "basket" && children?.length > 1) && (
                                <>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '9px',
                                            lineHeight: 1,
                                            backgroundColor: (theme) => alpha(theme.palette.background.paper, 1),
                                            color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                            padding: '1px 2px',
                                            borderRadius: '2px',
                                            fontWeight: 500,
                                            flexShrink: 0,
                                            minWidth: 'fit-content'
                                        }}
                                    >
                                        {exchangeToJSON(sec.exchange)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontSize: '9px',
                                            lineHeight: 1,
                                            backgroundColor: (theme) => alpha(theme.palette.background.paper, 1),
                                            color: (theme) => alpha(theme.palette.text.primary, 0.8),
                                            padding: '1px 2px',
                                            borderRadius: '2px',
                                            fontWeight: 500,
                                            flexShrink: 0,
                                            minWidth: 'fit-content'
                                        }}
                                    >
                                        {(() => {
                                            if (isIncomingOrder) {
                                                return productToJSON(strategyProductType);
                                            }
                                            const product = productToJSON(getPositionProduct(position));
                                            return product === 'PRODUCT_UNKNOWN' ? '-' : product;
                                        })()}
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.qtyCell)}>{quantityDisplay}</TableCell>
                    <TableCell className={cx(classes.tableCell, classes.averageCell)}>{displayAvgPrice}</TableCell>
                    <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                        <LtpCell position={position} />
                    </TableCell>
                    <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                        <ProfitCell position={position} />
                        {newQuantity !== 0 && (addedQuantity ?? 0) === 0 && (
                            <NonInteractiveTooltip key={position.security?.code ?? ''} title="Exit" arrow placement="bottom" sx={{ zIndex: 1 }}>
                                <Box className={cx("square-off-button", classes.squareOffButton)}>
                                    <ExtraSmallButton sx={{ width: '30px', height: '100%', borderRadius: '0 !important' }}
                                        onClick={onSquareOffClick}
                                    >
                                        <ExitIcon fontSize="small" />
                                    </ExtraSmallButton>
                                </Box>
                            </NonInteractiveTooltip>
                        )}
                    </TableCell>
                </TableRow>}
                {isPartialSquareOff && (
                    <>
                        <TableRow className={cx(classes.tableRow)}>
                            <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                                    <Typography variant="body1" sx={{ fontSize: '12px', lineHeight: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block', flexShrink: 1, minWidth: 0 }}>
                                        {displayName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '9px', lineHeight: 1, backgroundColor: (theme) => alpha(theme.palette.background.paper, 1), color: (theme) => alpha(theme.palette.text.primary, 0.8), padding: '1px 2px', borderRadius: '2px', fontWeight: 500, flexShrink: 0, minWidth: 'fit-content' }}>
                                        {exchangeToJSON(sec.exchange)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '9px', lineHeight: 1, backgroundColor: (theme) => alpha(theme.palette.background.paper, 1), color: (theme) => alpha(theme.palette.text.primary, 0.8), padding: '1px 2px', borderRadius: '2px', fontWeight: 500, flexShrink: 0, minWidth: 'fit-content' }}>
                                        {(() => { const product = productToJSON(getPositionProduct(position)); return product === 'PRODUCT_UNKNOWN' ? '-' : product; })()}
                                    </Typography>
                                </Stack>
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                                <span className={cx(newQuantity > 0 ? classes.buyQuantity: undefined, newQuantity < 0 ? classes.sellQuantity : undefined)}>{newQuantity}</span>
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.averageCell)}>{toLocaleStringIn(avgPrice / (security?.priceMultiplier ?? 100))}</TableCell>
                            <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                                <LtpCell position={position} />
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                                <ProfitCell position={{...position, squaredOffQuantity: 0}} />
                                {newQuantity !== 0 && (addedQuantity ?? 0) === 0 && (
                                    <NonInteractiveTooltip key={position.security?.code ?? ''} title="Exit" arrow placement="bottom" sx={{ zIndex: 1 }}>
                                        <Box className={cx("square-off-button", classes.squareOffButton)}>
                                            <ExtraSmallButton sx={{ width: '30px', height: '100%', borderRadius: '0 !important' }} onClick={onSquareOffClick}>
                                                <ExitIcon fontSize="small" />
                                            </ExtraSmallButton>
                                        </Box>
                                    </NonInteractiveTooltip>
                                )}
                            </TableCell>
                        </TableRow>
                        <TableRow className={cx(classes.tableRow, classes.closedPositionRow)}>
                            <TableCell className={cx(classes.tableCell, classes.symbolCell)}>
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ maxWidth: '100%', overflow: 'hidden' }}>
                                    <Typography variant="body1" sx={{ fontSize: '12px', lineHeight: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', display: 'block', flexShrink: 1, minWidth: 0 }}>
                                        {displayName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '9px', lineHeight: 1, backgroundColor: (theme) => alpha(theme.palette.background.paper, 1), color: (theme) => alpha(theme.palette.text.primary, 0.8), padding: '1px 2px', borderRadius: '2px', fontWeight: 500, flexShrink: 0, minWidth: 'fit-content' }}>
                                        {exchangeToJSON(sec.exchange)}
                                    </Typography>
                                    <Typography variant="caption" sx={{ fontSize: '9px', lineHeight: 1, backgroundColor: (theme) => alpha(theme.palette.background.paper, 1), color: (theme) => alpha(theme.palette.text.primary, 0.8), padding: '1px 2px', borderRadius: '2px', fontWeight: 500, flexShrink: 0, minWidth: 'fit-content' }}>
                                        {(() => { const product = productToJSON(getPositionProduct(position)); return product === 'PRODUCT_UNKNOWN' ? '-' : product; })()}
                                    </Typography>
                                </Stack>
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.qtyCell)}>
                                {(() => {
                                    return <>
                                        <span>0</span>
                                    </>;
                                })()}
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.averageCell)}>{toLocaleStringIn(avgPrice / (security?.priceMultiplier ?? 100))}</TableCell>
                            <TableCell className={cx(classes.tableCell, classes.ltpCell)}>
                                <LtpCell position={{...position, newQuantity: 0}} />
                            </TableCell>
                            <TableCell className={cx(classes.tableCell, classes.profitCell)} sx={{ position: 'relative' }}>
                                <ProfitCell position={{...position, newQuantity: 0}} />
                            </TableCell>
                        </TableRow>
                    </>
                )}
                {
                    (position.children?.length > 1) &&
                    position.children?.map((p, i) => <AnalyzePositionsTableRow key={`${p.groupId}-${getSecurityKey(p.security.exchange, p.security.code)}-${i}`} position={p} expandEnabled={expandEnabled} child />)
                }
            </>
}


export default memo(AnalyzePositionsTableRow)